import { HolidaySchema } from '@ametista-ai/api-schema';
import { ApiResponse } from '@ametista-ai/api-schema/http/types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/stores/api/apiClient';
import { holidaysKeys } from './keys';

type HolidaysParams = {
  clientId?: string;
  fromDate?: string;
  toDate?: string;
};

type CreateHolidayInput = {
  name: string;
  date: string;
  country?: string | null;
  clientIds?: string[];
};

type UpdateHolidayInput = {
  id: string;
  name?: string;
  date?: string;
  country?: string | null;
  clientIds?: string[];
};

function buildQueryString(params: HolidaysParams): string {
  const qs = new URLSearchParams();
  if (params.clientId) qs.set('clientId', params.clientId);
  if (params.fromDate) qs.set('fromDate', params.fromDate);
  if (params.toDate) qs.set('toDate', params.toDate);
  const str = qs.toString();
  return str ? `?${str}` : '';
}

export function useHolidays(params: HolidaysParams = {}) {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: holidaysKeys.list(params),
    queryFn: () =>
      apiClient.get<ApiResponse<{ holidays: HolidaySchema[] }>>(
        `/v1/holidays${buildQueryString(params)}`,
        undefined,
        undefined
      ),
  });

  const createHoliday = useMutation({
    mutationFn: (input: CreateHolidayInput) =>
      apiClient.post<ApiResponse<{ holiday: HolidaySchema }>>('/v1/holidays', input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: holidaysKeys.all }),
  });

  const updateHoliday = useMutation({
    mutationFn: ({ id, ...body }: UpdateHolidayInput) =>
      apiClient.patch<ApiResponse<{ holiday: HolidaySchema }>>(`/v1/holidays/${id}`, body),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: holidaysKeys.all }),
  });

  const deleteHoliday = useMutation({
    mutationFn: (id: string) => apiClient.delete(`/v1/holidays/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: holidaysKeys.all }),
  });

  return {
    holidays: query.data?.data?.holidays ?? [],
    isLoading: query.isLoading,
    createHoliday,
    updateHoliday,
    deleteHoliday,
  };
}
